/**
 * Correlatable, zero-noise-by-default diagnostics for the live-call stream.
 *
 * Every ingested event, binding decision, dedup drop, active-card transition and
 * finalization can be traced with stable correlation keys (callId, party,
 * rawId/turnId, utterance id, seq, isFinal/done, revision, textLen) so a live
 * call can be debugged from the browser console without a rebuild.
 *
 * Enable at runtime (no rebuild needed):
 *   window.__CONVEY_STREAM_DEBUG = true            // or
 *   localStorage.setItem('convey.streamDebug', '1')
 * Disable:
 *   window.__CONVEY_STREAM_DEBUG = false            // or removeItem(...)
 *
 * It is OFF by default (including in tests) so production logs stay clean.
 */

let forced: boolean | null = null;

/** Programmatic override (used by tests / hosts). null = fall back to env. */
export function setStreamDebug(on: boolean | null): void {
  forced = on;
}

export function isStreamDebug(): boolean {
  if (forced != null) return forced;
  try {
    const g = globalThis as any;
    if (g && typeof g.__CONVEY_STREAM_DEBUG !== "undefined") {
      return !!g.__CONVEY_STREAM_DEBUG;
    }
    if (typeof g?.localStorage?.getItem === "function") {
      const v = g.localStorage.getItem("convey.streamDebug");
      if (v === "1" || v === "true") return true;
    }
  } catch {
    /* SSR / restricted context — stay silent. */
  }
  return false;
}

const now = (): string => {
  try {
    return new Date().toISOString().substr(11, 12); // HH:MM:SS.mmm
  } catch {
    return "";
  }
};

/** Structured trace line. `data` is a flat, already-summarized correlation bag. */
export function streamLog(scope: string, msg: string, data?: Record<string, unknown>): void {
  if (!isStreamDebug()) return;
  // Debug tracing disabled (commented for now; keep for future diagnostics).
  // eslint-disable-next-line no-console
  // console.debug(`[convey-stream ${now()}] ${scope} ${msg}`, data || {});
}

/** Truncated length-only preview so logs never dump full transcripts. */
export function textInfo(s?: string): { len: number; head: string } {
  const str = (s || "").toString();
  return { len: str.length, head: str.slice(0, 24) };
}
